import { useTranslation } from 'next-i18next'
import { path } from 'ramda'
import {
  Box,
  IconHeart,
  IconHeartActive,
  useToast
} from '@machobear/design-system'
import { useDispatch, useSelector } from 'react-redux'

import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import { useDSLogin } from '@/features/Auth/Context/ds-login-provider'
import { useTermsDialogContext } from '@/components/terms-dialog/context'

import * as styles from './style.css.ts'

interface FavouriteButtonProps {
  listingId: string
  eventId: string
  showDSLoginModal: () => void
  listingCardDlv: string
  isCardLocked?: boolean
}

const FavouriteButton = ({
  listingId,
  eventId,
  showDSLoginModal,
  listingCardDlv,
  isCardLocked = false
}: FavouriteButtonProps) => {
  const dispatch = useDispatch()
  const role = useRole()
  const { t } = useTranslation()
  const isLoggedIn = useSessionComplete()
  const isLandlord = role === 'landlord'
  const favourites: Record<string, any> =
    useSelector(path(['listings', 'favourites'])) ?? {}
  const isFavourite = listingId in favourites
  const showToast = useToast()
  const { showModalForRequiredTerms } = useTermsDialogContext()
  const { setAfterLogin } = useDSLogin()
  const ensureLoggedIn = useEnsureLoggedIn({ showLogin: showDSLoginModal })

  const onFavourite = async () => {
    if (isFavourite) {
      const response = await dispatch.listings.removeFavourite(listingId)
      if (response?.error)
        showToast({ message: response.error.message, tone: 'critical' })
      else
        showToast({
          message: t(
            'favourites:listing_removed',
            'Listing removed from favourites!'
          ),
          action: {
            label: t('favourites:view_favourites', 'View your favourites'),
            onClick: () => {
              window.open(`/favourites`, '_blank')
            }
          },
          tone: 'positive'
        })
    } else {
      const response = await dispatch.listings.favourite(listingId)

      if (response?.error)
        showToast({ message: response.error.message, tone: 'critical' })
      else
        showToast({
          message: t(
            'favourites:listing_saved',
            'Listing saved to favourites!'
          ),
          action: {
            label: t('favourites:view_favourites', 'View your favourites'),
            onClick: () => {
              window.open(`/favourites`, '_blank')
            }
          },
          tone: 'positive'
        })
    }
  }

  const handleClickForUnlockedListing = () => {
    if (isLoggedIn) {
      onFavourite()
    } else {
      setAfterLogin(onFavourite)
      showDSLoginModal()
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (eventId) {
      window.dataLayer.push({
        event: eventId,
        listingCard: listingCardDlv
      })
    }

    isCardLocked
      ? ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', onFavourite)
        )
      : handleClickForUnlockedListing()
  }

  return !isLandlord ? (
    <>
      <Box
        onClick={handleClick}
        aria-label="Add to Favourites"
        className={styles.favouriteButton}
      >
        {isFavourite ? (
          <IconHeartActive size="small" />
        ) : (
          <IconHeart size="small" />
        )}
      </Box>
    </>
  ) : null
}

export default FavouriteButton
